import { CONSTANT } from '@const';
import { Page, PageDqp, PageDqpFilter, PageFilter } from '@types';
import axios, { AxiosError } from 'axios';
import * as Yup from 'yup';

import { msalService } from '..';

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface AxiosParams {
	method: HttpMethod;
	url: string;
	headers?: Record<string, string>;
	data?: object;
	/**
	 * Query params to add to the url
	 */
	queryParams?: unknown;
	/**
	 * Abort signal to cancel the request
	 */
	abortSignal?: AbortSignal;
	staleTimeInMs?: number;
}

export type ApiParams = AxiosParams;
interface ApiErrorData {
	Code: string;
	Message: string;
	message: string; // By inconsistency, APIs sometimes return the error message in this attribute
}
export type ApiError = AxiosError<ApiErrorData>;

export const pageSchema = Yup.object({
	items: Yup.array().required(),
	metadata: Yup.object({
		totalCount: Yup.number().required(),
		limit: Yup.number().required(),
		offset: Yup.number().required(),
	}).required(),
});

type cacheItem = {
	expiry: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: any;
};
const cache = new Map<string, cacheItem>();

export const emptyPage: PageDqp<never> = {
	items: [],
	metadata: {
		totalCount: 0,
		limit: 0,
		offset: 0,
	},
};

export class ApiService {
	protected static baseUrl = CONSTANT.API_BASE_URL;

	static getErrorStatusCode(error: ApiError | unknown) {
		return (error as ApiError)?.response?.status;
	}

	static getErrorMessage(error: ApiError | unknown) {
		const errorResponseData = (error as ApiError)?.response?.data;
		const errorMessage = errorResponseData?.Message ?? errorResponseData?.message;
		return errorMessage;
	}

	/**
	 * Makes an external API call to backend with any HTTP method, adding automaticallythe token as authorization on headers.
	 * @param {ApiParams} apiParams Parameters for the request to the API. At least, it has to include the HTTP method and url
	 * @returns {Promise<any>} Promise of response's data
	 * @throws {ApiError} Error from API if request is rejected
	 */
	protected static async executeApiRequest(apiParams: ApiParams) {
		const token = await msalService.getToken().catch(() => {
			return null;
		});
		const axiosParams = {
			...apiParams,
			headers: {
				accept: '*/*',
				Authorization: `Bearer ${token}`,
				'content-type': 'application/json',
				...apiParams.headers,
			},
			url: this.baseUrl + apiParams.url,
		};

		if (axiosParams.staleTimeInMs) {
			const cleanParms = JSON.stringify(apiParams);
			const cachedObject = cache.get(cleanParms);
			if (cachedObject && cachedObject.expiry > Date.now()) {
				return cachedObject.data;
			}
		}

		return axios({
			signal: apiParams.abortSignal,
			...axiosParams,
			params: apiParams.queryParams,
		})
			.then(response => {
				if (axiosParams.staleTimeInMs) {
					const cleanParms = JSON.stringify(apiParams);
					cache.set(cleanParms, {
						expiry: Date.now() + axiosParams.staleTimeInMs,
						data: response.data,
					});
				}
				return response.data;
			})
			.catch((error: ApiError) => {
				if (!axios.isCancel(error)) {
					throw error;
				}
			});
	}

	/**
	 * Retrieve the user's id from the current logged in session
	 */
	protected static getUserId() {
		const userIdentity = msalService.getUserIdentity();
		return userIdentity ? userIdentity.userId : '';
	}

	/**
	 * Creates a mock paginated response with random artificial delay (500-1000ms).
	 * For development purposes only.
	 * @param data The full dataset to paginate
	 * @param pageFilter Optional pagination parameters
	 * @returns Promise resolving to paginated Page response
	 */
	static async getPageMock<T>(data: T[], pageFilter?: PageFilter): Promise<Page<T>> {
		// Apply pagination
		const limit = pageFilter?.limit ?? 10;
		const offset = pageFilter?.offSet ?? PageFilter.initialOffSet;
		const items = data.slice(offset, offset + limit);

		// Random delay between range of milliseconds
		const times = { min: 500, max: 1000 };
		const responseTime = Math.floor(Math.random() * (times.max - times.min + 1)) + times.min;
		await new Promise(resolve => setTimeout(resolve, responseTime));

		return {
			items,
			metadata: {
				totalCount: data.length,
				limit,
				offSet: offset,
			},
		};
	}

	// TODO: if WEH API would get renamed as page.metadata.offset or [...].offSet – to harmonize both WEH and DQP APIs – then rename this as Page and remove above
	static async getPageDqpMock<T>(data: T[], pageFilter?: PageDqpFilter): Promise<PageDqp<T>> {
		// Apply pagination
		const limit = pageFilter?.limit ?? 10;
		const offset = pageFilter?.offset ?? PageFilter.initialOffSet;
		const items = data.slice(offset, offset + limit);

		// Random delay between range of milliseconds
		const times = { min: 500, max: 1000 };
		const responseTime = Math.floor(Math.random() * (times.max - times.min + 1)) + times.min;
		await new Promise(resolve => setTimeout(resolve, responseTime));

		return {
			items,
			metadata: {
				totalCount: data.length,
				limit,
				offset,
			},
		};
	}
}
